/**
 * /admin/dreamweaver · Concierge® desk for Dreamweaver.
 *
 * Doctrine-compliant surfaces:
 *   · Desk list is server-scoped by tenant (§4.7); private_journal
 *     dreams are excluded at the API layer, not just here.
 *   · Annotate rejects Weaving/Realizing transitions without notes (§4.6).
 *   · Alert requires a verification-assertion checkbox (§4.2). The tick
 *     confirms the hold/availability/price stated is real at the moment
 *     of sending. The endpoint queues a Zoho comment; it does NOT send
 *     email or WhatsApp directly (§Q7).
 *   · Erase is a physical purge (§Q5 step 2) with a 7-day SLA on
 *     downstream file deletions.
 *
 * Auth: uses the same admin JWT flow as every other /admin/* page.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import CldImg from "../components/CldImg";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const T = {
  ink: "#1a1a1a", cream: "#fbf9f4", mist: "#f4efe6",
  brass: "#a97e2d", brassSoft: "#c9a663",
  border: "rgba(26,26,26,0.10)", muted: "rgba(26,26,26,0.58)",
  danger: "#8a2c1f",
};

const STATUS_PILL = {
  Whispered:  { bg: "#f0ebe0", fg: "#8a6b2c" },
  Weaving:    { bg: "#e6efe0", fg: "#4a6b32" },
  Realizing:  { bg: "#e0e9ef", fg: "#2c5a7a" },
  Realized:   { bg: "#f4e0e6", fg: "#8a2c5a" },
  Reimagined: { bg: "#ede0ef", fg: "#5a2c8a" },
  Paused:     { bg: "#efebe0", fg: "#6b5a2c" },
  Archived:   { bg: "#e8e4dc", fg: "#605a52" },
};

const STATUS_OPTS = ["Whispered", "Weaving", "Realizing", "Realized", "Reimagined", "Paused"];

const REIMAGINED_DEFAULT =
  "We looked closely at your dream. The exact shape wasn't possible this time — " +
  "but we've found something we believe is close in spirit, and perhaps even lovelier. " +
  "May we share it with you?";

function authHeaders() {
  const t = localStorage.getItem("admin_token")
        || localStorage.getItem("concierge_admin_token")
        || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminDreamweaverPage() {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [scope, setScope]         = useState("");
  const [tenantFilter, setTF]     = useState(""); // header narrowing for super-admin
  const [statusFilter, setSF]     = useState("");
  const [agedDays, setAged]       = useState(0);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [openDream, setOpen]      = useState(null); // dream doc being edited
  const [mode, setMode]           = useState("");   // "annotate" | "alert" | "erase"

  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes]         = useState("");
  const [reimagined, setReimagined] = useState("");
  const [alertBy, setAlertBy]     = useState("");
  const [alertMsg, setAlertMsg]   = useState("");
  const [alertConfirm, setAC]     = useState(false);
  const [eraseReason, setER]      = useState("member_request");
  const [eraseBy, setEB]          = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  async function load() {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (agedDays > 0) params.set("aged_over_days", String(agedDays));
      const r = await axios.get(`${API}/api/admin/dreamweaver/desk?${params}`, {
        headers: {
          ...authHeaders(),
          ...(tenantFilter ? { "X-DW-Tenant": tenantFilter } : {}),
        },
      });
      setRows(r.data.dreams || []);
      setTotal(r.data.total || 0);
      setScope(r.data.scope || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, agedDays, tenantFilter]);

  function openAnnotate(d) {
    setOpen(d); setMode("annotate");
    setNewStatus(d.status || "");
    setNotes(d.concierge_notes || "");
    setReimagined(d.reimagined_offer || REIMAGINED_DEFAULT);
    setError("");
  }
  function openAlert(d) {
    setOpen(d); setMode("alert");
    setAlertBy(""); setAlertMsg(""); setAC(false);
    setError("");
  }
  function openErase(d) {
    setOpen(d); setMode("erase");
    setER("member_request"); setEB("");
    setError("");
  }
  function close() { setOpen(null); setMode(""); setError(""); }

  async function saveAnnotate() {
    setError("");
    const body = {};
    if (newStatus && newStatus !== openDream.status)   body.status = newStatus;
    if (notes && notes !== openDream.concierge_notes)  body.concierge_notes = notes;
    if (newStatus === "Reimagined")                    body.reimagined_offer = reimagined;
    if (Object.keys(body).length === 0)                return close();
    try {
      const r = await axios.post(
        `${API}/api/admin/dreamweaver/${openDream.id}/annotate`,
        body,
        { headers: { ...authHeaders(), ...(tenantFilter ? { "X-DW-Tenant": tenantFilter } : {}) } },
      );
      setRows((x) => x.map((row) => (row.id === r.data.id ? r.data : row)));
      setOpen(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function regenerateConcepts() {
    if (!openDream) return;
    setRegenBusy(true); setError("");
    try {
      const r = await axios.post(
        `${API}/api/admin/dreamweaver/${openDream.id}/concepts`,
        {},
        { headers: { ...authHeaders(), ...(tenantFilter ? { "X-DW-Tenant": tenantFilter } : {}) } },
      );
      setRows((x) => x.map((row) => (row.id === r.data.id ? r.data : row)));
      setOpen(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRegenBusy(false);
    }
  }

  async function saveAlert() {
    setError("");
    if (!alertConfirm) {
      setError("You must tick the verification box before queuing this alert.");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/api/admin/dreamweaver/${openDream.id}/alert`,
        {
          verified_by: alertBy,
          verification_assertion: alertConfirm,
          rendered_message: alertMsg,
        },
        { headers: { ...authHeaders(), ...(tenantFilter ? { "X-DW-Tenant": tenantFilter } : {}) } },
      );
      alert(`Alert queued to Zoho. Log event: ${r.data.log?.event}`);
      close();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function saveErase() {
    setError("");
    if (!eraseBy || eraseBy.length < 2) return setError("Sign your name to verify.");
    if (!confirm("This physically purges the dream and its audit trail from Mongo. Continue?")) return;
    try {
      await axios.post(
        `${API}/api/admin/dreamweaver/${openDream.id}/erase`,
        { reason: eraseReason, verified_by: eraseBy },
        { headers: { ...authHeaders(), ...(tenantFilter ? { "X-DW-Tenant": tenantFilter } : {}) } },
      );
      setRows((x) => x.filter((r) => r.id !== openDream.id));
      close();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  return (
    <div data-testid="admin-dreamweaver-page" style={{
      minHeight: "100vh", background: T.cream, color: T.ink,
      fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
      padding: "40px 24px 96px",
    }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", color: T.brass, marginBottom: 6 }}>
              Concierge® · Desk
            </div>
            <h1 style={{ fontSize: 34, fontWeight: 400, letterSpacing: "-0.01em", margin: 0,
                         fontFamily: "'EB Garamond', Georgia, serif" }}>
              Dreamweaver
            </h1>
            <div style={{ marginTop: 6, color: T.muted, fontSize: 13 }}>
              scope: <code>{scope}</code>  ·  {total} dream{total === 1 ? "" : "s"} on desk
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 24, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: T.muted }}>Tenant:</label>
          <input type="text" data-testid="filter-tenant" placeholder="e.g. demo · acme · leave blank for all"
                 value={tenantFilter} onChange={(e) => setTF(e.target.value)}
                 style={{ padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 6,
                          background: "white", fontSize: 13, minWidth: 240 }} />
          <label style={{ fontSize: 12, color: T.muted, marginLeft: 8 }}>Status:</label>
          <select value={statusFilter} onChange={(e) => setSF(e.target.value)}
                  data-testid="filter-status"
                  style={{ padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 6,
                           background: "white", fontSize: 13 }}>
            <option value="">All</option>
            {STATUS_OPTS.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          <label style={{ fontSize: 12, color: T.muted, marginLeft: 8 }}>Sitting &gt;</label>
          <select value={agedDays} onChange={(e) => setAged(parseInt(e.target.value, 10))}
                  data-testid="filter-aged"
                  style={{ padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 6,
                           background: "white", fontSize: 13 }}>
            <option value={0}>Any age</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
          <button onClick={load} data-testid="reload"
                  style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 6,
                           border: `1px solid ${T.border}`, background: "white", cursor: "pointer",
                           fontFamily: "inherit", fontSize: 13 }}>Reload</button>
        </div>

        {error && !openDream && (
          <div style={{ marginTop: 16, padding: "10px 14px", background: "#fdecea",
                        color: T.danger, borderRadius: 8, fontSize: 14 }}>{error}</div>
        )}

        {/* Table */}
        <div style={{ marginTop: 20, background: "white", border: `1px solid ${T.border}`,
                      borderRadius: 12, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, color: T.muted, textAlign: "center" }}>Loading dreams…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 60, color: T.muted, textAlign: "center", fontStyle: "italic" }}>
              No dreams sitting here. The desk is quiet.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: T.mist, textAlign: "left" }}>
                  <th style={cellHead}>Status</th>
                  <th style={cellHead}>Member</th>
                  <th style={cellHead}>Tenant</th>
                  <th style={cellHead}>Dream</th>
                  <th style={cellHead}>Urgency</th>
                  <th style={cellHead}>Whispered</th>
                  <th style={cellHead}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const pill = STATUS_PILL[d.status] || STATUS_PILL.Whispered;
                  return (
                    <tr key={d.id} data-testid={`desk-row-${d.id}`}
                        style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={cell}>
                        <span style={{
                          padding: "3px 10px", borderRadius: 999,
                          background: pill.bg, color: pill.fg,
                          fontSize: 10, letterSpacing: "0.16em",
                          textTransform: "uppercase", fontWeight: 500,
                        }}>{d.status}</span>
                      </td>
                      <td style={cell}>
                        <div>{d.member_name || "—"}</div>
                        <div style={{ fontSize: 11, color: T.muted }}>{d.member_email || ""}</div>
                      </td>
                      <td style={cell}><code style={{ fontSize: 12 }}>{d.tenant_slug || "—"}</code></td>
                      <td style={{ ...cell, maxWidth: 500 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis",
                                      whiteSpace: "nowrap" }}>
                          {d.dream_text}
                        </div>
                        {d.concierge_notes && (
                          <div style={{ marginTop: 4, fontSize: 12, color: T.muted, fontStyle: "italic" }}>
                            note: {String(d.concierge_notes).slice(0, 80)}…
                          </div>
                        )}
                      </td>
                      <td style={cell}>
                        <span style={{ fontSize: 12, color: T.muted, textTransform: "uppercase",
                                       letterSpacing: "0.14em" }}>
                          {d.urgency}
                        </span>
                      </td>
                      <td style={cell}>
                        <span style={{ fontSize: 12, color: T.muted }}>
                          {new Date(d.created_at).toLocaleDateString(undefined,
                            { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>
                        <button onClick={() => openAnnotate(d)} data-testid={`annotate-${d.id}`} style={btnLink(T)}>Annotate</button>
                        <button onClick={() => openAlert(d)}   data-testid={`alert-${d.id}`}   style={btnLink(T)}>Alert</button>
                        <button onClick={() => openErase(d)}   data-testid={`erase-${d.id}`}   style={{ ...btnLink(T), color: T.danger }}>Erase</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}
      {openDream && (
        <div style={modalBackdrop} onClick={close}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()} data-testid={`modal-${mode}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                          marginBottom: 20, gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.28em", color: T.brass,
                              textTransform: "uppercase", marginBottom: 6 }}>
                  {mode === "annotate" ? "Annotate dream" :
                   mode === "alert"    ? "Queue an alert" : "Physically erase"}
                </div>
                <div style={{ fontSize: 18, fontFamily: "'EB Garamond', serif",
                              maxWidth: 560, lineHeight: 1.4 }}>
                  {openDream.dream_text}
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                  {openDream.member_name} · {openDream.member_email} · {openDream.tenant_slug}
                </div>
              </div>
              <button onClick={close} data-testid="modal-close" style={{
                background: "transparent", border: 0, fontSize: 24, cursor: "pointer",
                color: T.muted, lineHeight: 1,
              }}>×</button>
            </div>

            {mode === "annotate" && (
              <div>
                {/* Media · voice + mood photos (concierge-only view) */}
                {(openDream.voice_url || (openDream.mood_photo_urls && openDream.mood_photo_urls.length > 0)) && (
                  <div style={{
                    padding: 14, background: T.mist, borderRadius: 10, marginBottom: 16,
                    border: `1px solid ${T.border}`,
                  }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
                                  color: T.muted, marginBottom: 10 }}>
                      What the member sent
                    </div>
                    {openDream.voice_url && (
                      <div style={{ marginBottom: 10 }}>
                        <audio data-testid="admin-voice-audio" controls src={openDream.voice_url}
                               style={{ width: "100%", maxWidth: 480 }} />
                      </div>
                    )}
                    {openDream.mood_photo_urls && openDream.mood_photo_urls.length > 0 && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {openDream.mood_photo_urls.map((u, i) => (
                          <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                             data-testid={`admin-photo-${i}`}
                             style={{ width: 80, height: 80, borderRadius: 8, overflow: "hidden",
                                      border: `1px solid ${T.border}`, display: "block" }}>
                            <CldImg src={u} w={160} alt="mood"
                                 style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Sonnet concepts · concierge-only, never shown to member */}
                <div style={{
                  padding: 14, background: "#fff7e7", border: `1px solid ${T.brassSoft}`,
                  borderRadius: 10, marginBottom: 16,
                }} data-testid="sonnet-concepts-panel">
                  <div style={{ display: "flex", justifyContent: "space-between",
                                alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, letterSpacing: "0.2em",
                                    textTransform: "uppercase", color: T.brass }}>
                        Sonnet concepts · concierge-only
                      </div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 3, maxWidth: 460 }}>
                        Directional starting points from Claude Sonnet. Never send this text
                        verbatim · use it to shape a warm, specific first call.
                      </div>
                    </div>
                    <button
                      onClick={regenerateConcepts}
                      disabled={regenBusy}
                      data-testid="regenerate-concepts"
                      style={{
                        padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.brass}`,
                        background: regenBusy ? T.brassSoft : "white",
                        color: T.brass, fontFamily: "inherit", fontSize: 12,
                        cursor: regenBusy ? "wait" : "pointer",
                      }}>
                      {regenBusy ? "Weaving…" : "Regenerate"}
                    </button>
                  </div>

                  {(!openDream.sonnet_concepts || !openDream.sonnet_concepts.concepts) ? (
                    <div style={{ fontSize: 13, color: T.muted, fontStyle: "italic" }}>
                      No concepts yet · click Regenerate to shape a few.
                    </div>
                  ) : (
                    <>
                      {openDream.sonnet_concepts.sensitivity_note && (
                        <div style={{
                          padding: "8px 12px", borderRadius: 6, background: "#fdecea",
                          color: T.danger, fontSize: 13, marginBottom: 10, lineHeight: 1.5,
                        }}>
                          ⚠ Sensitivity note: {openDream.sonnet_concepts.sensitivity_note}
                        </div>
                      )}
                      <div style={{ display: "grid", gap: 10 }}>
                        {(openDream.sonnet_concepts.concepts || []).map((c, i) => (
                          <div key={i} data-testid={`concept-${i}`}
                            style={{ padding: 12, background: "white",
                                     borderRadius: 8, border: `1px solid ${T.border}` }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
                              {c.title}
                            </div>
                            <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.5, marginBottom: 6 }}>
                              {c.shape}
                            </div>
                            {c.first_call && (
                              <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>
                                <strong style={{ color: T.brass }}>First call:</strong> {c.first_call}
                              </div>
                            )}
                            {c.member_note && (
                              <div style={{ fontSize: 12, color: T.muted, fontStyle: "italic" }}>
                                <strong>Ask the member:</strong> {c.member_note}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {openDream.sonnet_concepts.model && (
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 8, textAlign: "right" }}>
                          {openDream.sonnet_concepts.model}
                          {openDream.sonnet_concepts.auto_generated ? " · auto" : " · manual"}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <label style={lbl}>Status</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                        data-testid="annotate-status" style={fld}>
                  {STATUS_OPTS.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>

                <label style={lbl}>Concierge notes <span style={{ color: T.muted }}>(required for Weaving/Realizing per §4.6)</span></label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          data-testid="annotate-notes"
                          placeholder="What are you actually doing? Which suppliers are you calling? Whose availability are you waiting on?"
                          style={{ ...fld, minHeight: 100, resize: "vertical" }} />

                {newStatus === "Reimagined" && (
                  <>
                    <label style={lbl}>Reimagined offer <span style={{ color: T.muted }}>(the alternative you'll propose to the member — §4.6)</span></label>
                    <textarea value={reimagined} onChange={(e) => setReimagined(e.target.value)}
                              data-testid="annotate-reimagined"
                              style={{ ...fld, minHeight: 90, resize: "vertical" }} />
                  </>
                )}

                {error && <div style={errBox(T)}>{error}</div>}
                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button onClick={close} style={btnGhost(T)} data-testid="annotate-cancel">Cancel</button>
                  <button onClick={saveAnnotate} style={btnPrimary(T)} data-testid="annotate-save">Save</button>
                </div>
              </div>
            )}

            {mode === "alert" && (
              <div>
                <label style={lbl}>Rendered message to the member</label>
                <textarea value={alertMsg} onChange={(e) => setAlertMsg(e.target.value)}
                          data-testid="alert-message"
                          placeholder="Write what the member will read (via Zoho fan-out). Warm, concrete, no unverified numbers."
                          style={{ ...fld, minHeight: 140, resize: "vertical" }} />

                <label style={lbl}>Your name (verified_by)</label>
                <input type="text" value={alertBy} onChange={(e) => setAlertBy(e.target.value)}
                       data-testid="alert-verifiedby" style={fld}
                       placeholder="e.g. Rohan · Dipali · Aditya" />

                <label style={{
                  ...lbl, display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "12px 14px", background: "#fff8ea", borderRadius: 8,
                  border: `1px solid ${T.brassSoft}`, marginTop: 16, cursor: "pointer",
                }}>
                  <input type="checkbox" data-testid="alert-verify"
                         checked={alertConfirm} onChange={(e) => setAC(e.target.checked)}
                         style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>
                    <strong>I confirm the hold, availability, price, or date stated in this
                    message is real at this moment.</strong> I understand this ticks the
                    §4.2 verification assertion — a human promise, not an automated one.
                  </span>
                </label>

                {error && <div style={errBox(T)}>{error}</div>}
                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button onClick={close} style={btnGhost(T)} data-testid="alert-cancel">Cancel</button>
                  <button onClick={saveAlert} style={btnPrimary(T)} data-testid="alert-queue">Queue to Zoho</button>
                </div>
              </div>
            )}

            {mode === "erase" && (
              <div>
                <div style={{ padding: "12px 14px", borderRadius: 8, background: "#fdecea",
                              color: T.danger, marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
                  <strong>Irreversible.</strong> This physically removes the dream from Mongo
                  and records an audit row with a 7-day SLA to purge any linked audio/photos.
                </div>
                <label style={lbl}>Reason</label>
                <select value={eraseReason} onChange={(e) => setER(e.target.value)}
                        data-testid="erase-reason" style={fld}>
                  <option value="member_request">Member requested</option>
                  <option value="dpdp_erasure">DPDP formal erasure</option>
                  <option value="regulator_request">Regulator request</option>
                  <option value="incident_response">Incident response</option>
                  <option value="retention_policy">Retention policy</option>
                </select>
                <label style={lbl}>Your name (verified_by)</label>
                <input type="text" value={eraseBy} onChange={(e) => setEB(e.target.value)}
                       data-testid="erase-verifiedby" style={fld} placeholder="Sign your name" />
                {error && <div style={errBox(T)}>{error}</div>}
                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button onClick={close} style={btnGhost(T)} data-testid="erase-cancel">Cancel</button>
                  <button onClick={saveErase} data-testid="erase-confirm"
                          style={{ ...btnPrimary(T), background: T.danger, border: `1px solid ${T.danger}` }}>
                    Erase permanently
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────
const cellHead = { padding: "10px 14px", fontSize: 11, letterSpacing: "0.14em",
                   textTransform: "uppercase", color: T.muted, fontWeight: 500 };
const cell     = { padding: "12px 14px", verticalAlign: "top" };
const lbl      = { display: "block", fontSize: 11, letterSpacing: "0.2em",
                   textTransform: "uppercase", color: T.muted, marginTop: 14, marginBottom: 6 };
const fld      = { width: "100%", padding: "8px 10px", border: `1px solid ${T.border}`,
                   borderRadius: 6, background: "white", fontFamily: "inherit", fontSize: 14,
                   color: T.ink, boxSizing: "border-box" };
const modalBackdrop = {
  position: "fixed", inset: 0, background: "rgba(26,26,26,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  padding: 24,
};
const modalCard = {
  background: "white", borderRadius: 14, padding: 28, width: "100%", maxWidth: 640,
  maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(26,26,26,0.22)",
};
function btnLink(T)    { return { background: "transparent", border: 0, cursor: "pointer",
                          padding: "4px 10px", color: T.brass, fontFamily: "inherit",
                          fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em" }; }
function btnGhost(T)   { return { padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`,
                          background: "transparent", cursor: "pointer", fontFamily: "inherit",
                          fontSize: 14, color: T.ink }; }
function btnPrimary(T) { return { padding: "8px 20px", borderRadius: 8, border: `1px solid ${T.brass}`,
                          background: T.brass, cursor: "pointer", fontFamily: "inherit",
                          fontSize: 14, color: "white" }; }
function errBox(T)     { return { marginTop: 14, padding: "10px 14px", background: "#fdecea",
                          color: T.danger, borderRadius: 8, fontSize: 13 }; }
